import React from 'react'
import './PrivacyPolicy.css'

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="privacy-header">
        <h1>NepliShop Privacy Policy</h1>
        <p className="effective-date">Last Updated: January 2026</p>
        <p className="policy-intro">
          At NepliShop, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website neplishop.com or use our services.
        </p>
      </div>

      <div className="privacy-content">
        <section className="policy-section">
          <h2>1. Information We Collect</h2>
          <div className="section-content">
            <h3>Personal Information</h3>
            <ul>
              <li><strong>Contact Details:</strong> Name, email address, phone number, shipping address</li>
              <li><strong>Account Information:</strong> Username, password, profile picture</li>
              <li><strong>Payment Information:</strong> Credit/debit card details (securely processed through payment gateways), bank account information</li>
              <li><strong>Demographic Information:</strong> Age, gender, preferences</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns</li>
              <li><strong>Location Data:</strong> General location (city level) for delivery purposes</li>
              <li><strong>Cookies:</strong> Small data files stored on your device to enhance user experience</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>2. How We Use Your Information</h2>
          <div className="section-content">
            <ul>
              <li><strong>Order Processing:</strong> To process and deliver your purchases</li>
              <li><strong>Customer Service:</strong> To respond to inquiries and provide support</li>
              <li><strong>Personalization:</strong> To recommend products based on your preferences</li>
              <li><strong>Marketing:</strong> To send promotional offers (with your consent)</li>
              <li><strong>Improvement:</strong> To enhance website functionality and user experience</li>
              <li><strong>Legal Compliance:</strong> To comply with Nepali laws and regulations</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>3. Legal Basis for Processing (Under Nepali Law)</h2>
          <div className="section-content">
            <ul>
              <li><strong>Consent:</strong> When you explicitly agree to our data practices</li>
              <li><strong>Contractual Necessity:</strong> To fulfill our obligations under sales contracts</li>
              <li><strong>Legal Obligation:</strong> To comply with laws of Nepal including:
                <ul>
                  <li>Electronic Transactions Act, 2063 (2008)</li>
                  <li>Individual Privacy Act, 2075 (2018)</li>
                  <li>Consumer Protection Act, 2075 (2018)</li>
                </ul>
              </li>
              <li><strong>Legitimate Interest:</strong> For fraud prevention and business operations</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>4. Data Sharing and Disclosure</h2>
          <div className="section-content">
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Delivery Partners:</strong> Daraz Express, Nepal Post, and other courier services for order delivery</li>
              <li><strong>Payment Processors:</strong> eSewa, Khalti, ConnectIPS, Fonepay for secure payment processing</li>
              <li><strong>Service Providers:</strong> IT service providers, marketing agencies</li>
              <li><strong>Legal Authorities:</strong> When required by Nepali law enforcement agencies</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
            </ul>
            <p className="note">Note: We do not sell your personal information to third parties.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>5. Data Security Measures</h2>
          <div className="section-content">
            <p>We implement robust security measures including:</p>
            <ul>
              <li>SSL encryption for data transmission</li>
              <li>Secure servers located in Nepal</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>PCI-DSS compliance for payment processing</li>
              <li>Regular backup of customer data</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>6. Your Rights (Under Nepali Law)</h2>
          <div className="section-content">
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
            </ul>
            <p>To exercise these rights, contact us at privacy@neplishop.com</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>7. Data Retention</h2>
          <div className="section-content">
            <p>We retain your personal data only as long as necessary:</p>
            <ul>
              <li><strong>Active Accounts:</strong> Until account deletion request</li>
              <li><strong>Transaction Records:</strong> 5 years as required by Nepali tax laws</li>
              <li><strong>Marketing Data:</strong> Until consent withdrawal</li>
              <li><strong>Legal Requirements:</strong> As required by applicable Nepali laws</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>8. Cookies and Tracking Technologies</h2>
          <div className="section-content">
            <p>We use cookies for:</p>
            <ul>
              <li>Remembering your preferences and login status</li>
              <li>Analyzing website traffic and user behavior</li>
              <li>Delivering targeted advertisements</li>
              <li>Improving website performance</li>
            </ul>
            <p>You can manage cookie preferences through your browser settings.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>9. International Data Transfers</h2>
          <div className="section-content">
            <p>Your data is primarily stored and processed in Nepal. In cases where international transfer is necessary (e.g., international payment processing), we ensure adequate protection measures are in place and comply with Nepali data protection regulations.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>10. Children's Privacy</h2>
          <div className="section-content">
            <p>NepliShop does not knowingly collect personal information from children under 16 years of age. If you believe a child has provided us with personal information, please contact us immediately.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>11. Changes to This Policy</h2>
          <div className="section-content">
            <p>We may update this Privacy Policy periodically. We will notify you of significant changes by posting the new policy on our website and updating the "Last Updated" date.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>12. Contact Information</h2>
          <div className="section-content contact-details">
            <p><strong>NepliShop Pvt. Ltd.</strong></p>
            <p>Kathmandu, Nepal</p>
            <p>Email: neplishop.com@gmail.com</p>
            <p>Phone: +977 9741761406</p>
            <p>Website: www.neplishop.com</p>
            
           
          </div>
        </section>

        <section className="policy-section">
          <h2>13. Dispute Resolution</h2>
          <div className="section-content">
            <p>Any disputes regarding privacy matters will be governed by the laws of Nepal and subject to the exclusive jurisdiction of the courts in Kathmandu, Nepal.</p>
          </div>
        </section>

        <div className="acceptance-section">
          <p className="acceptance-note">
            <strong>By using NepliShop, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.</strong>
          </p>
          <p className="nepali-translation">
            <strong>नेपाली अनुवाद:</strong> यो गोपनीयता नीति पढेर बुझेको र यसका शर्तहरूसँग सहमत भएकोमा तपाईंले NepliShop प्रयोग गर्दा स्वीकार गर्नुहुन्छ।
          </p>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
